import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBlocks, journeyStations } from "@/hooks/useContent";
import { JourneyPath } from "@/components/JourneyPath";
import { ImportantNote } from "@/components/ImportantNote";
import { BranchDivider, BrandMark, ScriptMark } from "@/components/Brand";
import { t } from "@/lib/i18n";

const FACTS = [
  ["Since 1994", "Sound biblical counsel and compassionate care."],
  ["Free of charge", "Up to six pastoral counseling sessions."],
  ["Open to everyone", "Member or not, you are welcome here."],
];

export default function HomePage() {
  const { user } = useAuth();
  const { data: blocks } = useBlocks();
  const hero = blocks?.["home.hero"];
  const scripture = blocks?.["home.scripture"];
  const closing = blocks?.["home.closing"];
  const careHref = user ? "/portal" : "/sign-up";

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden bg-olive-900 text-cream-50" data-testid="home-hero">
        <img src="/brand/tree-cream.png" alt="" aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 w-[560px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.06] sm:w-[880px]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-terracotta-600" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-24">
          <BrandMark kind="tree" tone="terracotta" className="mx-auto h-24 w-auto sm:h-32" />
          <p className="mt-8 text-sm font-semibold tracking-wide text-coral-300 sm:text-base" data-testid="hero-ministry">{t("brand.ministry")}</p>
          <h1 className="display-italic mt-3 text-5xl text-cream-50 sm:text-6xl lg:text-7xl">{hero?.title || "Your Care Journey"}</h1>
          <BranchDivider tone="terracotta" centered className="mx-auto mt-8 max-w-sm" />
          <div className="mx-auto mt-8 max-w-[62ch] space-y-5 text-base leading-relaxed text-cream-50/95 md:text-lg">
            {(hero?.body || "").split(/\n\s*\n/).filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to={careHref} className="btn-terracotta w-full sm:w-auto" data-testid="hero-get-care-button">{t("nav.getCare")}</Link>
            <Link to="/reach-out" className="btn-outline-on-olive w-full sm:w-auto" data-testid="hero-reach-out-button">{t("nav.reachOutCta")}</Link>
          </div>
        </div>
      </section>

      <section className="border-b border-sage-400/50 bg-cream-100" aria-label="At a glance" data-testid="home-facts">
        <dl className="mx-auto grid max-w-7xl gap-8 px-4 py-10 text-center sm:grid-cols-3 sm:px-6">
          {FACTS.map(([k, v]) => (
            <div key={k}>
              <dt className="font-serif text-2xl font-semibold text-terracotta-600 sm:text-3xl">{k}</dt>
              <dd className="mt-1.5 text-sm text-ink-500 sm:text-base">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24" aria-labelledby="how-heading">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <BrandMark kind="branch" tone="terracotta" className="h-10 w-auto" />
            <h2 id="how-heading" className="mt-5 text-4xl sm:text-5xl">How It Works</h2>
            <p className="mt-4 max-w-[40ch] text-ink-500">Five steps, at your pace.</p>
            <Link to="/how-it-works" className="link mt-5 inline-block" data-testid="home-how-it-works-link">Read about each step</Link>
          </div>
          <div className="lg:col-span-8 lg:pl-8">
            <JourneyPath stations={journeyStations(blocks)} compact />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-fjord-600 text-cream-50" aria-label="Scripture">
        <img src="/brand/seal-cream.png" alt="" aria-hidden="true" className="pointer-events-none absolute -right-10 -top-10 hidden h-[440px] w-auto opacity-10 sm:block" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <blockquote className="max-w-3xl" data-testid="home-scripture">
            <p className="display-italic text-2xl leading-relaxed text-cream-50 sm:text-3xl">“{scripture?.body}”</p>
            <footer className="mt-5 text-sm font-semibold text-coral-300">{scripture?.title} (NIV)</footer>
          </blockquote>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h2 className="text-4xl sm:text-5xl">Open To Everyone</h2>
            <p className="mt-4 max-w-[60ch] text-ink-900">Resources, Life Care classes, Pray With Us, and Reach Out are open to everyone, whether or not you are a Patria member.</p>
            <ul className="mt-6 max-w-xl divide-y divide-sage-400/50 border-y border-sage-400/50">
              {[["/resources", t("nav.resources")], ["/life-care-classes", t("nav.classes")], ["/pray-with-us", t("nav.pray")], ["/reach-out", t("nav.reachOut")]].map(([to, label]) => (
                <li key={to}><Link to={to} className="tap w-full justify-between py-4 text-lg font-semibold text-olive-900 underline-offset-4 hover:underline" data-testid={`home-open-link-${to.slice(1)}`}>{label}</Link></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <ImportantNote />
          </div>
        </div>
        <BranchDivider tone="terracotta" centered className="mx-auto mt-20 max-w-lg" />
        {closing && (
          <div className="mx-auto mt-14 max-w-2xl text-center" data-testid="home-closing">
            <ScriptMark className="mx-auto h-16 w-auto sm:h-20" />
            <p className="display-italic mt-5 text-2xl text-olive-900 sm:text-3xl">{closing.body}</p>
          </div>
        )}
      </section>
    </div>
  );
}
